import { createFileRoute, Link } from "@tanstack/react-router";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { ArrowRight } from "lucide-react";
import { aeoGeo } from "@/lib/seo/schema";

const PHASES = [
  {
    n: "01",
    title: "Strategy",
    body: "A 30-minute call where we map your goals, channels and audience. You leave with a content plan, not a sales pitch.",
  },
  {
    n: "02",
    title: "Pre-production",
    body: "Concept, script and shot list. We handle casting, location and logistics so you can stay focused on your day job.",
  },
  {
    n: "03",
    title: "Shoot",
    body: "A senior crew in and out in a day. Calm sets, sharp direction, no overruns.",
  },
  {
    n: "04",
    title: "Edit",
    body: "First cut within five business days. Two revision rounds included. Color, sound and motion graphics in-house.",
  },
  {
    n: "05",
    title: "Delivery",
    body: "All formats your channels need — 16:9, 9:16, 1:1 — in the right codecs, captioned and ready to publish.",
  },
  {
    n: "06",
    title: "Iterate",
    body: "On Pulse, the cycle restarts the same week. Your channels never go quiet.",
  },
];

export const Route = createFileRoute("/en_/process")({
  head: () =>
    ({
      meta: [
        { title: "Our process — From brief to broadcast in 21 days" },
        {
          name: "description",
          content:
            "Fenny's six-phase video production process. Strategy, pre-production, shoot, edit, delivery, iterate. Senior crew, fixed timeline, predictable price.",
        },
        { property: "og:title", content: "How Fenny makes video" },
        {
          property: "og:description",
          content:
            "A repeatable six-phase process. Strategy to delivery in 21 days. No surprises.",
        },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: "https://fenny.se/en/process" },
      ],
      links: [
        { rel: "canonical", href: "https://fenny.se/en/process" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/process", name: "Our process — From brief to broadcast in 21 days", description: "Fenny's six-phase video production process. Strategy, pre-production, shoot, edit, delivery, iterate. Senior crew, fixed timeline, predictable price."})) },
      ],
    }) as never,
  component: ProcessPage,
});

function ProcessPage() {
  return (
    <div>
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-20">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_20%_10%,color-mix(in_oklab,var(--butter)_12%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="mb-6 text-xs uppercase tracking-[0.22em] text-butter">
            How we work
          </div>
          <KineticHeading
            text="Brief to broadcast in twenty-one days."
            className="max-w-[22ch] text-balance text-5xl font-semibold leading-[0.98] text-foreground md:text-7xl"
            highlight={{ indices: [4, 5], className: "text-mint italic font-serif font-normal" }}
          />
          <p className="mt-10 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
            Six repeatable phases. Senior crew at every step. A timeline you
            can actually plan around — and a price quoted before we start.
          </p>
        </div>
      </section>

      <Reveal>
        <section className="py-16 md:py-24">
          <div className="container-wide">
            <div className="grid gap-6 md:grid-cols-2">
              {PHASES.map((p) => (
                <div
                  key={p.n}
                  className="rounded-3xl border border-white/10 bg-surface p-8"
                >
                  <div className="text-xs uppercase tracking-[0.22em] text-butter">
                    Phase {p.n}
                  </div>
                  <h3 className="mt-3 text-2xl font-semibold">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-24 md:py-32 border-t border-white/10">
          <div className="container-wide text-center">
            <h2 className="mx-auto max-w-3xl text-balance text-4xl font-semibold leading-tight md:text-6xl">
              Curious how it looks for your team?
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Magnetic strength={0.3}>
                <Link
                  to="/en/contact"
                  className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground"
                >
                  Book a strategy call
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <Link
                to="/en/pricing"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-foreground/90 hover:bg-white/5"
              >
                See pricing
              </Link>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
